import { create } from 'zustand';
import { api } from '../services/api';

export interface Task {
  id: string;
  family_id: string;
  title: string;
  description: string;
  due_date: string;
  due_time: string;
  priority: string;
  status: string;
  assigned_to: string;
  assigned_to_name: string;
  created_by: string;
  created_by_name: string;
  category: string;
  tags: string[];
  pocket_money_pence?: number | null;
  blocked_reason?: string | null;
  rotation_user_ids?: string[] | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface TasksState {
  tasks: Task[];
  isLoading: boolean;
  error: string | null;
  fetchTasks: (filters?: { status?: string; assigned_to?: string }) => Promise<void>;
  createTask: (data: Partial<Task>) => Promise<Task>;
  updateTask: (taskId: string, data: Partial<Task>) => Promise<void>;
  updateTaskStatus: (taskId: string, status: string) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  // WebSocket handlers
  handleWsMessage: (message: any) => void;
}

export const useTasksStore = create<TasksState>((set, get) => ({
  tasks: [],
  isLoading: false,
  error: null,

  fetchTasks: async (filters?) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to);
      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/tasks${query}`);
      set({ tasks: res.data.tasks, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createTask: async (data) => {
    try {
      const res = await api.post('/tasks', data);
      const newTask = res.data.task;
      set(state => {
        if (state.tasks.some(t => t.id === newTask.id)) return state;
        return { tasks: [newTask, ...state.tasks] };
      });
      return newTask;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateTask: async (taskId, data) => {
    try {
      const res = await api.put(`/tasks/${taskId}`, data);
      const updatedTask = res.data.task;
      set(state => ({
        tasks: state.tasks.map(t => t.id === taskId ? updatedTask : t),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateTaskStatus: async (taskId, status) => {
    const prev = get().tasks;
    // Optimistic: flip the status instantly so the tap feels responsive;
    // on failure we restore the previous list.
    set(state => ({
      tasks: state.tasks.map(t => t.id === taskId ? { ...t, status } : t),
    }));
    try {
      const res = await api.put(`/tasks/${taskId}/status`, { status });
      const updatedTask = res.data.task;
      set(state => ({
        tasks: state.tasks.map(t => t.id === taskId ? updatedTask : t),
      }));
    } catch (err: any) {
      set({ tasks: prev, error: err.message });
      throw err;
    }
  },

  deleteTask: async (taskId) => {
    const prev = get().tasks;
    set(state => ({ tasks: state.tasks.filter(t => t.id !== taskId) }));
    try {
      await api.delete(`/tasks/${taskId}`);
    } catch (err: any) {
      set({ tasks: prev, error: err.message });
      throw err;
    }
  },

  handleWsMessage: (message) => {
    switch (message.type) {
      case 'TASK_CREATED':
        set(state => {
          const exists = state.tasks.find(t => t.id === message.payload.id);
          if (exists) return state;
          return { tasks: [message.payload, ...state.tasks] };
        });
        break;

      case 'TASK_UPDATED':
        set(state => ({
          tasks: state.tasks.map(t =>
            t.id === message.payload.id ? { ...t, ...message.payload } : t
          ),
        }));
        break;

      case 'TASK_STATUS_CHANGED':
        set(state => ({
          tasks: state.tasks.map(t =>
            t.id === message.payload.id ? { ...t, ...message.payload } : t
          ),
        }));
        break;

      case 'TASK_DELETED':
        set(state => ({
          tasks: state.tasks.filter(t => t.id !== message.payload.taskId),
        }));
        break;
    }
  },
}));
